import { Box, Group, Paper, ScrollArea, Stack, Text, useMantineTheme } from '@mantine/core';
import { IconCircleFilled, IconCirclePlus, IconReload } from '@tabler/icons-react';
import React, { useMemo } from 'react';
import { Handle, Position } from 'reactflow';
import { Show } from '../../../../../../react-commons/components/control-flow/Show';
import { When } from '../../../../../../react-commons/components/control-flow/When';
import { compareByField } from '../../../../../../react-commons/utils/utilFunctions';
import { VersionNodeVariantStatus } from '../VersionGraph';

export const variantedVersionGraphNodeWidth = 200;
export const maxVariantedVersionGraphNodeHeight = 200;
export const selectedNodeBorderWidth = 4;

export const getVersionGraphNodeColor = (isActive: boolean) => {
    return isActive ? '#59BE2399' : '#CED4DA99';
};

export const getVersionGraphNodeBorder = (selected: boolean) => {
    return selected ? `${selectedNodeBorderWidth}px solid black` : '0px solid black';
};

export interface VersionGraphNodeData {
    id: string;
    label: string;
    versionNumber: string;
    versionState: string;
    parentId?: string | null;
    variants?: Array<{ id: string; name: string; status?: VersionNodeVariantStatus }> | null;
    hasParent: boolean;
    hasChild: boolean;
    isActive: boolean;
}

export const VariantedVersionGraphNodeType = ({
    data,
    selected,
}: {
    data: VersionGraphNodeData;
    selected: boolean;
}) => {
    const theme = useMantineTheme();

    const variantNodes = useMemo(
        () =>
            data.variants
                ?.map((variant) => {
                    const parentNodeId = data.id ?? data.label;
                    const variantId = variant.id ?? variant.name;
                    return {
                        id: `${parentNodeId}-${variantId}`,
                        label: variant.name,
                        status: variant.status,
                    };
                })
                .toSorted((a, b) => compareByField('label', a, b)),
        [data]
    );

    const variantH = 32.8;
    const p = 4;
    const h = useMemo(() => {
        if (!data.variants?.length) {
            return 0;
        }
        const length = Math.min(data.variants.length, 4);
        return length * variantH + (length - 1) * p + 2;
    }, [data.variants?.length]);

    return (
        <>
            <Handle
                type="source"
                position={Position.Top}
                isConnectable={false}
                id={`${data.id}-top`}
                style={{ opacity: data.hasChild ? 1 : 0 }}
            />
            <Stack
                p={p}
                gap={4}
                w={variantedVersionGraphNodeWidth}
                className="selectable-node nowheel nodrag"
                bg={getVersionGraphNodeColor(data.isActive)}
                style={{
                    border: getVersionGraphNodeBorder(selected),
                    borderRadius: 10,
                    cursor: 'pointer',
                }}
            >
                <Text
                    style={{
                        height: 40,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}
                >
                    {data.label}
                </Text>
                <ScrollArea type={'auto'} h={h} scrollbars={'y'} scrollbarSize={2 * p}>
                    <Stack gap={4}>
                        {variantNodes?.map((variantNode) => (
                            <Paper
                                bg={'white'}
                                p={4}
                                radius="xl"
                                key={variantNode.id}
                                h={variantH}
                                w={variantedVersionGraphNodeWidth - 4 * p}
                            >
                                <Group w={'100%'} wrap={'nowrap'} gap={'xs'}>
                                    <Show when={variantNode.status} fallback={<IconCircleFilled />}>
                                        {(status) => (
                                            <Box w={24} h={24}>
                                                <When
                                                    expression={status}
                                                    cases={{
                                                        [VersionNodeVariantStatus.ADDED]: (
                                                            <IconCirclePlus color={'green'} />
                                                        ),
                                                        [VersionNodeVariantStatus.CHANGED]: (
                                                            <IconReload color={theme.colors.blue[5]} />
                                                        ),
                                                        [VersionNodeVariantStatus.UNCHANGED]: <IconCircleFilled />,
                                                    }}
                                                />
                                            </Box>
                                        )}
                                    </Show>
                                    <Text style={{ textOverflow: 'ellipsis', overflow: 'hidden' }}>
                                        {variantNode.label}
                                    </Text>
                                </Group>
                            </Paper>
                        ))}
                    </Stack>
                </ScrollArea>
            </Stack>
            <Handle
                type="target"
                position={Position.Bottom}
                isConnectable={false}
                id={`${data.id}-bottom`}
                style={{ opacity: data.hasParent ? 1 : 0 }}
            />
        </>
    );
};
